import { Gauge } from "lucide-react";
import {
  PageHeading, Section, Field, Steps, Callout, RoleBadge,
  Diagram, MockSelect, MockButton, MockKpi, MockRow,
} from "../shared";

export default function SettingsLiveRefreshSection() {
  return (
    <div data-testid="guide-settings-live-refresh">
      <PageHeading
        icon={Gauge}
        title="Live Refresh & Performance"
        description="Decide how often the app quietly checks for new information in the background — without anyone needing to reinstall or redeploy. Pick a ready-made setting or fine-tune it."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="it_admin" />
        <span className="text-xs text-white/40">Needs the <span className="font-mono text-white/70">manage_runtime_config</span> permission. Every change is recorded with who, when, and an optional reason.</span>
      </div>

      <Callout variant="info" title="In plain words">
        Screens can refresh themselves so you always see the latest data. Refreshing more often feels more "live" but works the computers harder. This page lets you choose the balance. The safe default ("Conservative") is usually fine.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Rarely. Touch it if the app feels slow under load (turn refreshing down), or if everyone is on a fast network and wants the freshest data (turn it up). During a problem you can hit Emergency Pause.
        </p>
      </Section>

      <Section title="The screen">
        <Diagram
          frameLabel="Settings → Live Refresh & Performance"
          legend={[
            <>Pick a preset (Off, Conservative, Balanced, Real-time) or choose Custom.</>,
            <>Run a <span className="text-white">Health check</span> before turning refresh up.</>,
            <>Watch the <span className="text-white">Projected load</span> estimate change as you tweak.</>,
            <>Press <span className="text-white">Save</span>.</>,
            <>Use <span className="text-white">Emergency Pause</span> to stop all background refresh instantly.</>,
          ]}
        >
          <MockSelect label="Preset" value="Conservative" marker={1} />
          <MockRow>
            <MockButton label="Run health check" marker={2} variant="ghost" />
            <MockKpi label="Projected load" value="~430/min" marker={3} />
          </MockRow>
          <MockRow>
            <MockButton label="Save" marker={4} />
            <MockButton label="Emergency Pause" marker={5} variant="danger" />
          </MockRow>
        </Diagram>
      </Section>

      <Section title="The presets">
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1">
          <li><span className="text-white">Off</span> — nothing refreshes on its own. Screens still update when you do something; they just don't poll in the background.</li>
          <li><span className="text-white">Conservative</span> (factory default) — <span className="text-white">120s / 300s / 900s</span>. Gentle on slow connections and laptop batteries; good for a typical 40–50 person setup.</li>
          <li><span className="text-white">Balanced</span> — middle-of-the-road, good for a stable wired office network.</li>
          <li><span className="text-white">Real-time</span> — the freshest, tightest refresh. Use when nearly everyone is on a fast, stable network.</li>
          <li><span className="text-white">Custom</span> — hand-tune the three speeds below yourself.</li>
        </ul>
      </Section>

      <Section title="The three speed groups (tiers)">
        <div className="space-y-2">
          <Field name="Live work" required="required"><p>The things people actively work on — action items, sub-tasks, MD meetings. Refreshes the most often.</p></Field>
          <Field name="Reference" required="required"><p>Things you mostly read — the KPI dashboard, history, reports. Refreshes a bit less often.</p></Field>
          <Field name="Configuration" required="required"><p>Admin and setup pages that barely change. Refreshes the least often.</p></Field>
        </div>
        <p className="text-xs text-white/55">Each speed is picked from a fixed list and can never go below the built-in safety floor of <span className="text-white">10 seconds</span> (when not Off). That floor can't be loosened from this screen.</p>
      </Section>

      <Section title="Other options">
        <div className="space-y-2">
          <Field name="Refresh on tab focus" required="optional"><p>Re-check for new data when you switch back to a tab you'd left. On by default.</p></Field>
          <Field name="Refresh in background tabs" required="optional"><p>Keep refreshing even while the tab is hidden. Off by default to save laptop battery.</p></Field>
          <Field name="Apply method" required="optional"><p>Only <span className="text-white">Refresh required</span> is active today — people get new settings the next time they load a page. The <span className="text-white">Live broadcast (SSE)</span> option appears greyed out and is a planned future feature.</p></Field>
          <Field name="KPI grade against internal deadline" required="optional"><p>When on, KPI scoring uses the private <span className="text-white">Due back by</span> date instead of the public due date, for items that captured one.</p></Field>
        </div>
      </Section>

      <Section title="The tools on the page">
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1">
          <li><span className="text-white">Health check</span> — quickly times a few sample reads so you can judge whether it's safe to go to Real-time.</li>
          <li><span className="text-white">Projected load</span> — estimates how many requests per minute your current settings will cause, based on the number of active users. Updates as you tweak.</li>
          <li><span className="text-white">Change history</span> — the last 50 changes, each with one-click undo.</li>
          <li><span className="text-white">Emergency Pause</span> — turns all background refresh off instantly. Use it during an incident.</li>
          <li><span className="text-white">Reset to defaults</span> — back to Conservative.</li>
        </ul>
      </Section>

      <Section title="How to save a change">
        <Steps steps={[
          <>Pick a preset, or choose Custom and set the three speeds.</>,
          <>Optionally type a reason (e.g. "moved to Balanced after the network upgrade").</>,
          <>Press <span className="text-white">Save</span>. The change is recorded and people pick it up the next time they load a page.</>,
        ]} />
      </Section>

      <Callout variant="warning" title="Don't jump to Real-time blindly">
        Real-time can multiply the load on a busy day. Run the health check first, watch the projected load, and undo from the history if anything feels off.
      </Callout>
    </div>
  );
}
